import React from 'react';
import { ReportTheme } from '../../types';

interface TopCountriesTableWidgetProps {
  data: any;
  data_points?: string[];
  report_start_date?: string;
  report_end_date?: string;
  reportTheme: ReportTheme;
}

export const TopCountriesTableWidget: React.FC<TopCountriesTableWidgetProps> = ({ 
  data, 
  data_points,
  report_start_date,
  report_end_date,
  reportTheme 
}) => {
  console.log('TopCountriesTableWidget raw data:', data);

  // Process the raw API data internally for Top Countries
  const processData = () => {
    if (!data || !data.data || typeof data.data !== 'object') {
      console.warn('TopCountriesTableWidget: Invalid data structure');
      return null;
    }

    const rawData = data.data;
    const dateEntries = Object.entries(rawData);
    
    if (dateEntries.length === 0) {
      console.warn('TopCountriesTableWidget: No data entries found');
      return null;
    }

    // Aggregate country data across all dates
    const countryTotals: { [key: string]: { sessions: number; pageViews: number; bounceRate: number; bounceRateCount: number } } = {};

    dateEntries.forEach(([dateKey, dayData]: [string, any]) => {
      if (!dayData || typeof dayData !== 'object') {
        return;
      }

      // Process each country in the day's data
      Object.entries(dayData).forEach(([countryKey, countryData]: [string, any]) => {
        if (countryKey !== '(not set)' && countryData && typeof countryData === 'object') {
          const sessions = countryData.sessions || countryData.count || 0;
          const pageViews = countryData.pageviews || 0;
          const bounceRate = countryData.bounce_rate || 0;

          if (!countryTotals[countryKey]) {
            countryTotals[countryKey] = { sessions: 0, pageViews: 0, bounceRate: 0, bounceRateCount: 0 };
          }

          countryTotals[countryKey].sessions += sessions;
          countryTotals[countryKey].pageViews += pageViews;
          countryTotals[countryKey].bounceRate += bounceRate * sessions; // Weighted average
          countryTotals[countryKey].bounceRateCount += sessions;
        }
      });
    });

    if (Object.keys(countryTotals).length === 0) {
      console.warn('TopCountriesTableWidget: No country data found');
      return null;
    }
    
    const tableData = Object.entries(countryTotals)
      .map(([countryKey, data]) => ({
        country: countryKey,
        sessions: data.sessions,
        pageViews: data.pageViews,
        bounceRate: data.bounceRateCount > 0 
          ? ((data.bounceRate / data.bounceRateCount) * 100).toFixed(1) + '%'
          : '0.0%'
      }))
      .sort((a, b) => b.sessions - a.sessions) // Sort by sessions descending
      .slice(0, 10); // Take top 10 countries

    return tableData.length > 0 ? tableData : null;
  };

  const processedData = processData();

  if (!processedData || processedData.length === 0) {
    return null;
  }

  // Function to get country flag emoji
  const getCountryFlag = (countryName: string): string => {
    const countryToFlag: { [key: string]: string } = {
      'India': '🇮🇳',
      'United States': '🇺🇸',
      'United Kingdom': '🇬🇧',
      'Canada': '🇨🇦',
      'Germany': '🇩🇪',
      'France': '🇫🇷',
      'Australia': '🇦🇺',
      'Singapore': '🇸🇬',
      'United Arab Emirates': '🇦🇪',
      'Saudi Arabia': '🇸🇦',
      'Qatar': '🇶🇦',
      'Kuwait': '🇰🇼',
      'Oman': '🇴🇲',
      'Bahrain': '🇧🇭',
      'China': '🇨🇳',
      'Hong Kong': '🇭🇰',
      'Japan': '🇯🇵',
      'South Korea': '🇰🇷',
      'Indonesia': '🇮🇩',
      'Malaysia': '🇲🇾',
      'Thailand': '🇹🇭',
      'Vietnam': '🇻🇳',
      'Philippines': '🇵🇭',
      'Poland': '🇵🇱',
      'Netherlands': '🇳🇱',
      'Sweden': '🇸🇪',
      'Norway': '🇳🇴',
      'Denmark': '🇩🇰',
      'Finland': '🇫🇮',
      'Switzerland': '🇨🇭',
      'Austria': '🇦🇹',
      'Belgium': '🇧🇪',
      'Ireland': '🇮🇪',
      'Spain': '🇪🇸',
      'Italy': '🇮🇹',
      'Portugal': '🇵🇹',
      'Greece': '🇬🇷',
      'Turkey': '🇹🇷',
      'Türkiye': '🇹🇷',
      'Russia': '🇷🇺',
      'Ukraine': '🇺🇦',
      'Brazil': '🇧🇷',
      'Argentina': '🇦🇷',
      'Mexico': '🇲🇽',
      'Chile': '🇨🇱',
      'Colombia': '🇨🇴',
      'Peru': '🇵🇪',
      'South Africa': '🇿🇦',
      'Egypt': '🇪🇬',
      'Nigeria': '🇳🇬',
      'Kenya': '🇰🇪',
      'Tanzania': '🇹🇿',
      'New Zealand': '🇳🇿',
      'Israel': '🇮🇱',
      'Iran': '🇮🇷',
      'Pakistan': '🇵🇰',
      'Bangladesh': '🇧🇩',
      'Sri Lanka': '🇱🇰',
      'Nepal': '🇳🇵',
      'Myanmar': '🇲🇲',
      'Cambodia': '🇰🇭',
      'Laos': '🇱🇦',
      'Mongolia': '🇲🇳',
      'Kazakhstan': '🇰🇿',
      'Uzbekistan': '🇺🇿',
      'Azerbaijan': '🇦🇿',
      'Georgia': '🇬🇪',
      'Armenia': '🇦🇲',
      'Serbia': '🇷🇸',
      'Croatia': '🇭🇷',
      'Slovenia': '🇸🇮',
      'Slovakia': '🇸🇰',
      'Czech Republic': '🇨🇿',
      'Hungary': '🇭🇺',
      'Romania': '🇷🇴',
      'Bulgaria': '🇧🇬',
      'Lithuania': '🇱🇹',
      'Latvia': '🇱🇻',
      'Estonia': '🇪🇪',
      'Iceland': '🇮🇸',
      'Luxembourg': '🇱🇺',
      'Malta': '🇲🇹',
      'Cyprus': '🇨🇾',
      'Côte d\'Ivoire': '🇨🇮'
    };
    
    return countryToFlag[countryName] || '🌍';
  };

  return (
    <div className="w-full h-full overflow-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b">
            <th className="text-left p-2 font-medium">Country</th>
            <th className="text-right p-2 font-medium">Sessions</th>
            <th className="text-right p-2 font-medium">Page Views</th>
            <th className="text-right p-2 font-medium">Bounce Rate</th>
          </tr>
        </thead>
        <tbody>
          {processedData.map((row, index) => (
            <tr key={index} className="border-b hover:bg-muted/50">
              <td className="p-2">
                <div className="flex items-center space-x-2">
                  <span className="text-lg">{getCountryFlag(row.country)}</span>
                  <div className="truncate max-w-[180px]" title={row.country}>
                    {row.country}
                  </div>
                </div>
              </td>
              <td className="text-right p-2">{row.sessions.toLocaleString()}</td>
              <td className="text-right p-2">{row.pageViews.toLocaleString()}</td>
              <td className="text-right p-2">{row.bounceRate}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};
